// Rail step "Dotazník k mobilní aplikaci" - ONE act: the ten step wizard
// that collects the store material (F135c).
//
// Nothing about the Apple account and nothing about the release lives here.
// The Apple checklist still travels INTO the wizard's fourth step as a node
// (the S21 chain is untouched), which is a different thing from rendering the
// Apple step's page inside this one.
//
// F135b - the waiting state may only claim what is true. The card used to
// print "Předvyplňujeme dotazník z tvých podkladů" for ANY unlocked state
// while reading no fact at all; measured on a real client with ZERO
// app_wizard_steps rows, so the promise was empty. The page now reads the one
// server fact (`appPrefill`) and has three honest faces:
//   pending/delayed - a prefill really is on its way -> WorkingAvatar (F53:
//                     the ONE marker for "we are working on it", never a bare
//                     pill) and, past the threshold, the delayed sentence.
//   prefilled       - the machine already wrote the answers -> the wizard
//                     opens and says so.
//   absent          - nothing is coming -> the wizard opens with no promise.
import { useState } from "react";
import { AppWizard } from "../app-wizard/WizardShell";
import { WorkingAvatar } from "../../WorkingAvatar";
import { ActionError, DataError, InlineLockIcon, LockedStepPreview, refreshClientFact } from "../shared";
import { workflowPillClass } from "../../workflow-pill";
import { AppleChecklist } from "./AppleStep";
import { appleChecklistLock, appleFacts, type AppTrackProps } from "./app-track-shared";

export function QuestionnaireStep(props: AppTrackProps) {
  // Wizard submission state arrives with the wizard payload (single source);
  // until it loads the status card stays quiet instead of guessing.
  const [submittedAt, setSubmittedAt] = useState<string | null | "unknown">("unknown");
  const [useEmptyWizard, setUseEmptyWizard] = useState(false);
  const { section, account, confirmed } = appleFacts(props);
  const state = props.salesSteps.find((item) => item.key === "app_questionnaire")?.state ?? "next";
  const submitted = submittedAt !== "unknown" && submittedAt !== null;
  const prefill = props.appPrefill ?? null;
  const waiting = prefill === "pending" || prefill === "delayed";
  // The wizard is open unless a REAL prefill is still running. Without that
  // fact there is nothing to wait for, so the client is not held behind an
  // interstitial that promises work nobody is doing.
  const open = state !== "locked" && (!waiting || useEmptyWizard);

  return (
    <section className="m5b-view app-step-view" data-app-step="questionnaire" aria-labelledby="app-questionnaire-title">
      <header className="m5b-heading focused-sales-heading">
        <div>
          <p className="eyebrow blue-eyebrow">{props.t("app.questionnaire.eyebrow")}</p>
          <h1 id="app-questionnaire-title">{props.t("rail.steps.app_questionnaire")}</h1>
          {/* The lead says what the step IS. Why it is shut is the lock card's
              job, and saying it twice is what the mobile shell caught. */}
          <p className="lead">{props.t(prefill === "prefilled"
            ? "app.questionnaire.prefilledCopy"
            : "app.questionnaire.focusCopy")}</p>
        </div>
        <span className={`status-pill ${workflowPillClass(state)}`}>{props.t(`states.${state}`)}</span>
      </header>
      {props.dataErrors?.has("resources") && <DataError t={props.t} />}

      {submitted && (
        // D16e-prep: honest waiting state after submission. EXTENSION POINT
        // (Rado): live App Store / Google Play review status lands here once
        // the shared DB + status polling exist; until then no invented states.
        <article className="appwiz-submitted-card" role="status">
          <span className="appwiz-submitted-mark" aria-hidden="true">✓</span>
          <div>
            <h2>{props.t("app.submittedTitle")}</h2>
            <p>{props.t("app.submittedCopy")}</p>
          </div>
        </article>
      )}

      {state === "locked" && (
        // K05: a step nobody can open yet still says what will be here, who
        // opens it and when - the same anatomy as every other locked step.
        <article className="focused-sales-lock" data-locked-preview="true" data-sales-step="app_questionnaire" role="status">
          <p className="inline-lock-copy"><InlineLockIcon />{props.t("app.questionnaire.locked")}</p>
          <LockedStepPreview step="app_questionnaire" t={props.t} />
        </article>
      )}

      {waiting && !useEmptyWizard && (
        <article
          className="app-questionnaire-step app-questionnaire-working"
          data-sales-step="app_questionnaire"
          data-prefill-state={prefill}
          role="status"
        >
          <WorkingAvatar size="card" profilePhotoUrl={props.profilePhotoUrl} />
          <div className="app-questionnaire-working-text">
            <h2>{props.t("app.questionnaire.working")}</h2>
            <p>{props.t("app.questionnaire.prefilling")}</p>
            {prefill === "delayed" && (
              <p className="handover-fact-delayed">{props.t("app.questionnaire.prefillDelayed")}</p>
            )}
            {props.membershipRole === "owner" && (
              <button className="secondary-button" type="button" onClick={() => setUseEmptyWizard(true)}>
                {props.t("app.questionnaire.useEmpty")}
              </button>
            )}
          </div>
        </article>
      )}

      {open && (
        <div data-sales-step="app_questionnaire">
          <AppWizard
            locale={props.locale}
            csrfToken={props.csrfToken ?? ""}
            membershipRole={props.membershipRole}
            appleConfirmed={confirmed}
            appleChecklist={(
              <AppleChecklist
                initial={account}
                lock={appleChecklistLock(props, section)}
                busy={props.busyAction !== null}
                submitting={props.busyAction === "apple-start"}
                t={props.t}
                onSubmit={(payload) => props.onAction("apple-start", payload)}
              />
            )}
            partner={props.partner}
            t={props.t}
            onLoaded={(wizardSubmittedAt) => setSubmittedAt(wizardSubmittedAt)}
            onSubmitted={async () => {
              setSubmittedAt(new Date().toISOString());
              await refreshClientFact(props);
            }}
          />
        </div>
      )}
      <ActionError t={props.t} issue={props.actionError} />
    </section>
  );
}
